/**
 * Helpers for the booking-wave schedule editor.
 *
 * Separate from the component so that module exports only components
 * (React Fast Refresh, whose lint rule runs at --max-warnings 0).
 *
 * Backend contract: apps/campaigns/wave_schedule.py. Two parts matter
 * here:
 *
 * 1. **A day absent means no wave.** It is not "send at 00:00", and the
 *    grid must send `""` (or omit the key) for an unticked day rather
 *    than a midnight time.
 * 2. **The timezone and the next run come from the SERVER.** They depend
 *    on DST and on a zone the browser may not be in — a client computing
 *    either would reintroduce the bug this feature fixes.
 */

export const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type DayKey = (typeof DAY_KEYS)[number];

/** day key -> "HH:MM". A missing key is a day with no wave. */
export type Days = Partial<Record<DayKey, string>>;

export interface WaveScheduleResponse {
  schedule: { version: number; days: Days };
  inherited: boolean;
  timezone: string;
  timezone_source: "campaign" | "organisation" | "utc";
  local_now: string;
  next_run: string | null;
  day_keys: DayKey[];
  latest_local_hour: number;
  late_grace_hours: number;
  last_wave_on?: string | null;
}

export type Preset = "weekdays" | "daily" | "custom";

const WEEKDAYS = DAY_KEYS.slice(0, 5) as readonly DayKey[];
export const FALLBACK_TIME = "10:00";

/**
 * Which preset a set of days corresponds to — DERIVED, never stored.
 *
 * Storing it would let it drift out of step with the days it claims to
 * describe, and the days are the thing the wave actually reads.
 */
export function presetFor(days: Days): Preset {
  const set = DAY_KEYS.filter((d) => days[d]);
  const times = new Set(set.map((d) => days[d]));
  if (times.size !== 1) return "custom";
  if (set.length === 5 && WEEKDAYS.every((d) => days[d])) return "weekdays";
  if (set.length === 7) return "daily";
  return "custom";
}

/** The single time a preset shows. Falls back sensibly for a custom
 *  schedule being flattened into one. */
export function dominantTime(days: Days): string {
  const times = DAY_KEYS.map((d) => days[d]).filter(Boolean) as string[];
  if (times.length === 0) return FALLBACK_TIME;
  const counts = new Map<string, number>();
  for (const t of times) counts.set(t, (counts.get(t) ?? 0) + 1);
  // Sorted-then-indexed rather than `[0][0]`: `noUncheckedIndexedAccess`
  // types an index read as possibly undefined, and `times.length > 0`
  // above is not something the compiler carries this far.
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  return ranked[0]?.[0] ?? FALLBACK_TIME;
}

/** Apply a preset, keeping the time the operator already chose. */
export function applyPreset(preset: Preset, days: Days): Days {
  const time = dominantTime(days);
  if (preset === "weekdays") {
    return Object.fromEntries(WEEKDAYS.map((d) => [d, time])) as Days;
  }
  if (preset === "daily") {
    return Object.fromEntries(DAY_KEYS.map((d) => [d, time])) as Days;
  }
  // Custom starts from whatever is set, so switching to it never empties
  // the grid and make the operator start again.
  return { ...days };
}

export function setDayTime(days: Days, day: DayKey, time: string): Days {
  return { ...days, [day]: time };
}

/** Untick a day. Deletes the key rather than blanking it, because the
 *  absence IS the meaning. */
export function toggleDay(days: Days, day: DayKey, on: boolean): Days {
  const next = { ...days };
  if (on) next[day] = next[day] || dominantTime(days);
  else delete next[day];
  return next;
}

export function toPayload(days: Days) {
  // Only ticked days travel. A blanked time would be rejected as a bad
  // HH:MM, and a 00:00 would be a wave at midnight.
  const out: Record<string, string> = {};
  for (const d of DAY_KEYS) {
    const t = days[d];
    if (t) out[d] = t;
  }
  return { schedule: { version: 1, days: out } };
}

export function isPaused(days: Days): boolean {
  return DAY_KEYS.every((d) => !days[d]);
}

/** Days whose time the server would refuse, so the form can say so
 *  before a save. Mirrors `_parse_time` in wave_schedule.py. */
export function timeProblems(days: Days): Set<DayKey> {
  const bad = new Set<DayKey>();
  for (const d of DAY_KEYS) {
    const t = days[d];
    if (t === undefined) continue;
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(t)) bad.add(d);
  }
  return bad;
}

/**
 * A human sentence for a schedule — "every weekday at 09:00".
 *
 * Exists because three strings used to hardcode "10:00 Mon–Fri", which
 * became wrong the moment the schedule was configurable: an operator
 * changed theirs to 09:00 and the campaign overview still claimed 10:00.
 *
 * Returns null when the times differ per day, rather than trying to
 * build "Mon at 09:00, Fri at 14:00" in fifteen languages. The caller
 * shows a "custom schedule" phrase and the settings screen carries the
 * detail — a vaguer sentence is better than a wrong or mistranslated one.
 */
export function summariseDays(
  days: Days,
  locale: string,
): { kind: "daily" | "weekdays" | "some"; time: string; dayNames: string } | null {
  const on = DAY_KEYS.filter((d) => days[d]);
  if (on.length === 0) return null;
  const times = new Set(on.map((d) => days[d]));
  if (times.size !== 1) return null;
  const time = days[on[0] as DayKey] as string;

  if (on.length === 7) return { kind: "daily", time, dayNames: "" };
  if (
    on.length === 5 &&
    (DAY_KEYS.slice(0, 5) as readonly DayKey[]).every((d) => days[d])
  ) {
    return { kind: "weekdays", time, dayNames: "" };
  }
  return { kind: "some", time, dayNames: shortDayNames(on, locale) };
}

/**
 * Localised short weekday names, via Intl rather than seven more
 * translation keys per language — the browser already knows "lun." and
 * "Mo.", and getting them from a table would be fifteen chances to be
 * wrong about somebody else's language.
 *
 * 2024-01-01 was a Monday, which is the anchor DAY_KEYS is ordered from.
 */
export function shortDayNames(keys: readonly DayKey[], locale: string): string {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const names = keys.map((k) =>
    fmt.format(new Date(Date.UTC(2024, 0, 1 + DAY_KEYS.indexOf(k)))),
  );
  // Intl.ListFormat gives the locale's own "and" / "et" / "und".
  try {
    return new Intl.ListFormat(locale, {
      style: "short",
      type: "conjunction",
    }).format(names);
  } catch {
    return names.join(", ");
  }
}
